import Close from '@mui/icons-material/Close';
import {
	Alert,
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	IconButton,
	Radio,
	Tab,
	Tabs,
	TextField,
	Typography,
	useTheme,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DefaultValues, FormProvider, useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { GridColDef } from '@mui/x-data-grid';
import AppDataGrid, {
	initialPagination,
} from '@/common/components/dataGrid/AppDataGrid';
import AppSummarySearchBar from '@/common/components/form-inputs/AppSummarySearchBar';
import { useGetAdmissionRequestAllQuery } from '@/api/member-management/admissionRequest';
import { AdmissionRequestPageDTO } from '@/api/DTO/member-management/admissionRequest';
import { MemberStatusEnum } from '@/api/enum/common.enum';

interface Props {
	open: boolean;
	onClose: () => void;
	onConfirm: (row: AdmissionRequestPageDTO) => void | Promise<void>;
	/** Shows the "Excel" tab: link by the old File no. of a member kept only in Excel. */
	onConfirmLegacy?: (legacyFileNo: string) => void | Promise<void>;
}

/**
 * Picker of service-closed members of the current centre that are not yet
 * linked by another record (DMS-REQ-WEB-CHA-001 §3.2 / §5.1), or an old
 * File no. kept in Excel (§3.3).
 */
export default function ClosedMemberPickerDialog({
	open,
	onClose,
	onConfirm,
	onConfirmLegacy,
}: Props) {
	const { t } = useTranslation();
	const theme = useTheme();
	const [tab, setTab] = useState<'SYSTEM' | 'EXCEL'>('SYSTEM');
	const [legacyFileNo, setLegacyFileNo] = useState('');
	const [selected, setSelected] = useState<AdmissionRequestPageDTO | null>(
		null
	);
	const [submitting, setSubmitting] = useState(false);

	const defaultValues: DefaultValues<any> = {
		pagination: { ...initialPagination, size: 5 },
	};
	const methods = useForm<any>({ defaultValues });
	const watchSearchString = methods.watch('searchString');
	const watchPagination = methods.watch('pagination');

	useEffect(() => {
		methods.setValue('pagination', {
			...methods.getValues('pagination'),
			page: 1,
		});
	}, [watchSearchString]);

	useEffect(() => {
		if (open) {
			setTab('SYSTEM');
			setLegacyFileNo('');
			setSelected(null);
			methods.reset(defaultValues);
		}
	}, [open]);

	const { data: list, isFetching } = useGetAdmissionRequestAllQuery(
		{
			statusList: [MemberStatusEnum.All],
			excludeLinkedClosedUser: true,
			searchString: watchSearchString,
			pagination: watchPagination,
			sortings: [],
		},
		{ refetchOnMountOrArgChange: true, skip: !open || tab !== 'SYSTEM' }
	);

	const isExcel = tab === 'EXCEL';
	const trimmedLegacyFileNo = legacyFileNo.trim();

	const handleConfirm = async () => {
		if (isExcel ? !trimmedLegacyFileNo : !selected) return;
		setSubmitting(true);
		try {
			if (isExcel) await onConfirmLegacy?.(trimmedLegacyFileNo);
			else if (selected) await onConfirm(selected);
		} finally {
			setSubmitting(false);
		}
	};

	const columns: GridColDef[] = [
		{
			field: 'select',
			headerName: '',
			width: 60,
			renderCell: (params) => (
				<Radio
					checked={params.row.id === selected?.id}
					onChange={() => setSelected(params.row)}
				/>
			),
		},
		{
			field: 'fileNo',
			headerName: t('member.fileNo'),
			flex: 20,
			renderCell: (params) => (
				<Link
					to={`/member-management/member-list/detail/${params.row.id}`}
					target='_blank'
					rel='noopener noreferrer'
					onClick={(e) => e.stopPropagation()}
					style={{ color: theme.palette.primary.main }}
				>
					{params.row.fileNo}
				</Link>
			),
		},
		{
			field: 'nameEn',
			headerName: t('member.name'),
			flex: 25,
			renderCell: (params) => (
				<Box>
					<Typography variant='body2'>{params.row.nameEn}</Typography>
					<Typography variant='body2'>{params.row.nameZh}</Typography>
				</Box>
			),
		},
		{ field: 'phone', headerName: t('member.mobilePhone'), flex: 20 },
		{ field: 'statusDesc', headerName: t('member.status'), flex: 20 },
	];

	return (
		<Dialog open={open} onClose={onClose} maxWidth='lg' fullWidth>
			<DialogTitle variant='h5'>
				{t('memberDuplication.pickerTitle')}
			</DialogTitle>
			<IconButton
				aria-label='close'
				onClick={onClose}
				sx={{
					position: 'absolute',
					right: 8,
					top: 8,
					color: (theme) => theme.palette.grey[500],
				}}
			>
				<Close />
			</IconButton>
			<DialogContent>
				{onConfirmLegacy && (
					<Tabs
						value={tab}
						onChange={(_, value) => setTab(value)}
						sx={{ marginBottom: '16px' }}
					>
						<Tab
							value='SYSTEM'
							label={t('memberDuplication.closedInSystemTab')}
							sx={{ textTransform: 'none' }}
						/>
						<Tab
							value='EXCEL'
							label={t('memberDuplication.closedInExcelTab')}
							sx={{ textTransform: 'none' }}
						/>
					</Tabs>
				)}
				{isExcel ? (
					<>
						<Typography variant='body2' sx={{ marginBottom: '12px' }}>
							{t('memberDuplication.legacyFileNoText')}
						</Typography>
						<Alert severity='warning' sx={{ marginBottom: 2 }}>
							{t('memberDuplication.legacyFileNoWarning')}
						</Alert>
						<TextField
							fullWidth
							label={t('memberDuplication.legacyFileNo')}
							value={legacyFileNo}
							onChange={(e) => setLegacyFileNo(e.target.value)}
						/>
					</>
				) : (
					<>
						<Typography variant='body2' sx={{ marginBottom: '12px' }}>
							{t('memberDuplication.pickerText')}
						</Typography>
						<FormProvider {...methods}>
							<Grid container spacing={2} marginTop={0} alignItems='center'>
								<Grid size={12}>
									<AppSummarySearchBar
										name='searchString'
										control={methods.control}
										placeholder={t('common.search')}
									/>
								</Grid>
								<Grid size={12}>
									<AppDataGrid
										loading={isFetching}
										rows={list?.data.items || []}
										rowCount={list?.data.pagination.totalItems ?? 0}
										columns={columns}
										getRowId={(row) => row.id}
										control={methods.control}
									/>
								</Grid>
							</Grid>
						</FormProvider>
					</>
				)}
			</DialogContent>
			<DialogActions>
				<Button sx={{ textTransform: 'none' }} onClick={onClose}>
					{t('button.cancel')}
				</Button>
				<Button
					variant='contained'
					disabled={(isExcel ? !trimmedLegacyFileNo : !selected) || submitting}
					onClick={handleConfirm}
				>
					{t('button.confirm')}
				</Button>
			</DialogActions>
		</Dialog>
	);
}
